import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import { translate } from "app/filters/i18nFilter";
import { AllowedCountriesDropdown } from "app/pages/sales-intelligence/modules/enrichment/components/AllowedCountriesDropdown/AllowedCountriesDropdown";
import { ColumnPickers } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/ColumnPickers";
import CompanyColumnPickerHead from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/CompanyColumnPickerHead";
import { CompanyEnrichmentSummary } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/CompanyEnrichmentSummary";
import { Footer } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/Footer";
import { Header } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/Header";
import InfoTooltip from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/InfoTooltip";
import { HeadersDropdown } from "app/pages/sales-intelligence/modules/enrichment/components/HeadersDropdown/HeadersDropdown";
import { useColumnConfigs } from "app/pages/sales-intelligence/modules/enrichment/hooks/useColumnConfigs";
import { useCsvEnrichmentQuota } from "app/pages/sales-intelligence/modules/enrichment/hooks/useCsvEnrichmentQuota";
import { useEnrichmentCountries } from "app/pages/sales-intelligence/modules/enrichment/hooks/useEnrichmentCountries";
import { useSelectedConfigFields } from "app/pages/sales-intelligence/modules/enrichment/hooks/useSelectedConfigFields";
import {
    getPreviewThunk,
    getSelectedColumnsConfigThunk,
} from "app/pages/sales-intelligence/modules/enrichment/store/effects/enrichment";
import {
    selectIsLoadingPreview,
    selectPreview,
    selectSelectedColumnsConfig,
} from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import type {
    ConfigField,
    CsvExportColumnCategory,
} from "app/pages/sales-intelligence/modules/enrichment/types/columns";
import { createFieldBadgeComponent } from "app/pages/sales-intelligence/modules/enrichment/utils/componentFactories";
import {
    ENRICHMENT_RESULT,
    PREVIEW_HINT,
    TOTAL,
    TOTAL_SHORT,
    TRAFFIC_TEXT,
    WEBSITES_TEXT,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import { sortConfigFieldsByName } from "app/pages/sales-intelligence/modules/enrichment/utils/utils";
import QuotaLeftLink from "app/pages/sales-intelligence/modules/quota/components/QuotaLeftLink";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import type { ParseDomainsResult } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import type { ICountryObject } from "app/services/CountryService";

import {
    StyledCountriesWrapper,
    StyledDomainDropdownWrapper,
    StyledDownloadEnrichedContainer,
    StyledDropdownSection,
    StyledDropdownWrapper,
    StyledExampleDomains,
    StyledHelpText,
    StyledModalBody,
    StyledTitle,
} from "./style";
import QuotaTooltip from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/QuotaTooltip";
import QuotaTotalCost from "app/pages/sales-intelligence/modules/quota/components/QuotaTotalCost";

type DownloadEnrichedProps = {
    parsingResult: ParseDomainsResult;
    selectedHeader: string;
    fileName: string;
    isEnriching: boolean;
    onConfirm: (props: { columns: readonly ConfigField[]; domainFieldName: string; country: number }) => void;
    onReplace: VoidFunction;
    onClose: VoidFunction;
    setSelectedHeader: (header: string) => void;
};

export const EnrichmentCsvContainer = ({
    onReplace,
    onClose,
    onConfirm,
    setSelectedHeader,
    selectedHeader,
    fileName,
    parsingResult,
    isEnriching,
}: DownloadEnrichedProps) => {
    const dispatch = useDispatch();

    const preview = useSelector(selectPreview);
    const isLoadingPreview = useSelector(selectIsLoadingPreview);
    const selectedColumnsConfigFields = useSelector(selectSelectedColumnsConfig);

    const { isConfigLoading, config } = useColumnConfigs();

    const { selectedConfigFields, setSelectedConfigByName, setAllConfigs } = useSelectedConfigFields(config);

    const { localCountry, setLocallySelectedCountry } = useEnrichmentCountries();

    const { allDomainValues, headerNames } = parsingResult;
    const currentDomains = allDomainValues[selectedHeader];

    const getPreviewWrapped = () => dispatch(getPreviewThunk(currentDomains));

    useMount(() => {
        void dispatch(getSelectedColumnsConfigThunk());
    });

    useEffect(() => {
        if (selectedColumnsConfigFields?.length > 0 && config?.length > 0) {
            setAllConfigs(
                sortConfigFieldsByName({ defaultFields: selectedColumnsConfigFields, columnsConfig: config }),
            );
        }
    }, [selectedColumnsConfigFields, config]);

    useEffect(() => {
        void getPreviewWrapped();
    }, [selectedHeader]);

    const allSelectedFields = Object.values(selectedConfigFields).flat();
    const allDomainColumns = Object.keys(allDomainValues);

    const { isDataCreditsEnabled, isOutOfQuota, quota, totalCost, dataCreditsLinkUrl, getFieldCost } =
        useCsvEnrichmentQuota({
            selectedFields: allSelectedFields,
            matchedRecordsCount: preview?.matchedRows ?? 0,
        });

    const renderFieldBadge = createFieldBadgeComponent({ isDataCreditsEnabled, getFieldCost });

    const [selectedCountry, setSelectedCountry] = useState<ICountryObject>(localCountry);

    const onConfigApply = (configName: CsvExportColumnCategory, selectedFields: ConfigField[]) =>
        setSelectedConfigByName(configName, selectedFields);

    const onCountrySelect = (country: ICountryObject) => {
        setSelectedCountry(country);
        enrichmentTrackingService.csv.company.trackSelectCountry(country.text);
        setLocallySelectedCountry(String(country.id));
    };

    const selectedHeaderExample =
        currentDomains.length > 2
            ? `${currentDomains?.slice(0, 3).join(", ")} + ${allDomainValues[selectedHeader].length - 3}`
            : currentDomains.join(", ");

    const handleApply = () =>
        onConfirm({
            columns: allSelectedFields,
            country: selectedCountry.id,
            domainFieldName: selectedHeader,
        });

    const isDownloadDisabled = !(!!selectedHeader && !!selectedCountry && !!allSelectedFields.length);

    return (
        <StyledDownloadEnrichedContainer>
            <Header
                iconSize="xl"
                iconName="refresh_list"
                className="replaceWrapper"
                title={fileName}
                onClick={onReplace}
            />
            <StyledModalBody type="company">
                <StyledDropdownSection>
                    <StyledDropdownWrapper>
                        <StyledHelpText>{translate(WEBSITES_TEXT)}</StyledHelpText>
                        <StyledDomainDropdownWrapper className="headers-dropdown-container">
                            <HeadersDropdown
                                appendTo=".headers-dropdown-container"
                                headerNames={headerNames}
                                selectedHeader={selectedHeader}
                                setSelectedHeader={setSelectedHeader}
                                getIsOptionDisabled={(header) => !allDomainColumns.includes(header)}
                                type="company"
                            />
                            <StyledExampleDomains>{selectedHeaderExample}</StyledExampleDomains>
                        </StyledDomainDropdownWrapper>
                    </StyledDropdownWrapper>

                    <StyledTitle>{translate(ENRICHMENT_RESULT)}</StyledTitle>

                    <StyledDropdownWrapper>
                        <StyledHelpText>{translate(TRAFFIC_TEXT)}</StyledHelpText>
                        <StyledCountriesWrapper className="countries-dropdown-container">
                            <AllowedCountriesDropdown
                                appendTo=".countries-dropdown-container"
                                dataAutomation="enrich-countries-dd-btn"
                                onCountrySelect={onCountrySelect}
                                selectedCountry={selectedCountry}
                            />
                        </StyledCountriesWrapper>
                    </StyledDropdownWrapper>
                </StyledDropdownSection>

                <ColumnPickers
                    isLoading={isConfigLoading}
                    selectedConfigFields={selectedConfigFields}
                    config={config}
                    head={
                        <CompanyColumnPickerHead numberOfSelectedItems={allSelectedFields.length} />
                    }
                    renderFieldBadge={renderFieldBadge}
                    onApply={onConfigApply}
                />
            </StyledModalBody>

            <Footer
                isEnriching={isEnriching}
                isLoading={isLoadingPreview}
                isDisabled={isDownloadDisabled}
                isOutOfQuota={isOutOfQuota}
                onClose={onClose}
                onConfirm={handleApply}
            >
                <CompanyEnrichmentSummary
                    isLoading={isLoadingPreview}
                    isOutOfQuota={isOutOfQuota}
                    previewValues={preview}
                    badge={
                        isDataCreditsEnabled ? (
                            <QuotaTooltip
                                isOutOfQuota={isOutOfQuota}
                                quota={quota.remaining}
                            >
                                <QuotaTotalCost
                                    isOutOfQuota={isOutOfQuota}
                                    totalCost={totalCost}
                                />
                            </QuotaTooltip>
                        ) : null
                    }
                    summaryText={translate(isDataCreditsEnabled ? TOTAL_SHORT : TOTAL, {
                        matched: preview?.matchedRows ?? 0,
                    })}
                    infoTooltip={
                        <InfoTooltip
                            defaultText={translate(PREVIEW_HINT)}
                            quota={quota.remaining}
                            withLegacyQuotaView={!isDataCreditsEnabled ? isOutOfQuota : false}
                        />
                    }
                />
            </Footer>
        </StyledDownloadEnrichedContainer>
    );
};
